/**
 * WSAdminBoardPolicyPage — 워크스페이스 게시판 정책
 *
 * 카드 단위로 정책 분리, 각 카드 내 [저장] 버튼.
 * - 카드 1: 새 보드 권한 기본값 (board_defaults)
 * - 카드 2: 보드 관리자 위임 (board_manager_user_ids) — 다음 사이클
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { useMyRole } from '../../../context/WorkspacePermissionContext';
import { useWorkspaceSettings, useRefreshWorkspaceSettings } from '../../../context/WorkspaceSettingsContext';
import { Workspace, WorkspaceMember } from '../../../models/Workspace';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { toast } from '../../../components/ui/use-toast';
import { LayoutList, Shield, UserCog, Loader2, Plus, X, Search } from 'lucide-react';
import {
  WritePermission, CommentPermission, NotifyPermission,
  WRITE_PERMISSIONS, COMMENT_PERMISSIONS, NOTIFY_PERMISSIONS,
  getWritePermissionOption, getCommentPermissionOption, getNotifyPermissionOption,
} from '../../../models/Board';
import { BoardDefaults, DEFAULT_BOARD_DEFAULTS } from '../../../models/WorkspaceSettings';
import { updateWorkspaceSettings } from '../../../services/WorkspaceSettingsService';
import { Fire } from '../../../services/FireClient';
import { UserAvatar } from '../../components/UserAvatar';
import { cn } from '../../../lib/utils';

interface Props {
  workspace: Workspace;
}

const WSAdminBoardPolicyPage: React.FC<Props> = ({ workspace }) => {
  const myRole = useMyRole();
  const canManage = myRole === 'owner' || myRole === 'admin';

  if (!canManage) {
    return (
      <div className="px-6 py-8">
        <p className="text-muted-foreground"><Trans>Only workspace administrators can access this page.</Trans></p>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-6 max-w-[760px]">
      {/* 페이지 헤더 */}
      <header className="flex items-center gap-2.5">
        <LayoutList className="size-5 text-primary" strokeWidth={2} />
        <h1 className="text-body-lg font-semibold text-foreground"><Trans>Board Policy</Trans></h1>
        <span className="text-meta-sm text-muted-foreground">{workspace.name}</span>
      </header>

      <BoardDefaultsCard workspace={workspace} />
      <BoardManagersCard workspace={workspace} />
    </div>
  );
};

// ── 카드 1: 새 보드 권한 기본값 ─────────────────────────────────

const BoardDefaultsCard: React.FC<{ workspace: Workspace }> = ({ workspace }) => {
  const settings = useWorkspaceSettings();
  const refresh = useRefreshWorkspaceSettings();
  const [draft, setDraft] = useState<BoardDefaults>(settings.board_defaults);
  const [saving, setSaving] = useState(false);

  // 외부 설정 변경 시 draft sync
  useEffect(() => { setDraft(settings.board_defaults); }, [settings.board_defaults]);

  const dirty =
    draft.write_permission        !== settings.board_defaults.write_permission ||
    draft.comment_permission      !== settings.board_defaults.comment_permission ||
    draft.notify_all_permission   !== settings.board_defaults.notify_all_permission;

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await updateWorkspaceSettings(workspace.id, { board_defaults: draft });
      await refresh();
      toast({ title: t`Defaults saved` });
    } catch (err) {
      console.error('[BoardPolicy] 기본값 저장 실패:', err);
      toast({ title: t`Failed to save`, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft({ ...DEFAULT_BOARD_DEFAULTS });
  };

  return (
    <section className="rounded-xl border border-border bg-card p-5 space-y-4">
      <header className="flex items-center gap-2">
        <Shield className="size-4 text-primary" />
        <h2 className="text-body font-semibold text-foreground"><Trans>Default permissions for new boards</Trans></h2>
      </header>
      <p className="text-meta-sm text-muted-foreground leading-relaxed">
        <Trans>Default permission values pre-filled when creating a new board. Users can change them when creating a board.</Trans>
        {' '}
        <strong className="font-semibold text-foreground">
          <Trans>These defaults apply to newly created boards only — existing boards are not affected. To change an existing board's permissions, edit that board's settings.</Trans>
        </strong>
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* 글 작성 */}
        <PermRow label={<Trans>Write posts</Trans>}>
          <Select
            value={draft.write_permission}
            onValueChange={v => setDraft(d => ({ ...d, write_permission: v as WritePermission }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {WRITE_PERMISSIONS.map(k => (
                <SelectItem key={k} value={k}>{getWritePermissionOption(k).label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PermRow>

        {/* 댓글 */}
        <PermRow label={<Trans>Comments</Trans>}>
          <Select
            value={draft.comment_permission}
            onValueChange={v => setDraft(d => ({ ...d, comment_permission: v as CommentPermission }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {COMMENT_PERMISSIONS.map(k => (
                <SelectItem key={k} value={k}>{getCommentPermissionOption(k).label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PermRow>

        {/* @all 발송 — @개인 멘션은 작성권한과 자동 일치이므로 별도 권한 없음 */}
        <PermRow label={<Trans>Send @all mentions</Trans>}>
          <Select
            value={draft.notify_all_permission}
            onValueChange={v => setDraft(d => ({ ...d, notify_all_permission: v as NotifyPermission }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {NOTIFY_PERMISSIONS.map(k => (
                <SelectItem key={k} value={k}>{getNotifyPermissionOption(k).label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PermRow>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" onClick={handleReset} disabled={saving}>
          <Trans>Reset to defaults</Trans>
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
          {saving && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
          <Trans>Save</Trans>
        </Button>
      </div>
    </section>
  );
};

const PermRow: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-meta-sm font-semibold text-muted-foreground">{label}</label>
    {children}
  </div>
);

// ── 카드 2: 보드 관리자 위임 ─────────────────────────────────

const BoardManagersCard: React.FC<{ workspace: Workspace }> = ({ workspace }) => {
  const settings = useWorkspaceSettings();
  const refresh = useRefreshWorkspaceSettings();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [draftIds, setDraftIds] = useState<string[]>(settings.board_manager_user_ids);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');

  // 멤버 목록 로드
  useEffect(() => {
    setLoading(true);
    Fire.ws.getWorkspaceMembers(workspace.id)
      .then(setMembers)
      .catch(err => {
        console.error('[BoardPolicy] 멤버 로드 실패:', err);
        toast({ title: t`Failed to load members`, variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, [workspace.id]);

  // 외부 설정 변경 시 draft sync
  useEffect(() => { setDraftIds(settings.board_manager_user_ids); }, [settings.board_manager_user_ids]);

  // 후보(member role 만, 이미 추가된 것 제외)
  const candidates = useMemo(() => {
    const draftSet = new Set(draftIds);
    const q = pickerQuery.trim().toLowerCase();
    return members
      .filter(m => m.role === 'member')
      .filter(m => !draftSet.has(m.id))
      .filter(m => !q || m.user.name.toLowerCase().includes(q) || m.user.email?.toLowerCase().includes(q));
  }, [members, draftIds, pickerQuery]);

  // 현재 위임된 멤버 (member role 만 의미 있음. 그 외는 stale id 일 수 있으나 표시는 함)
  const designatedMembers = useMemo(() => {
    return draftIds
      .map(id => members.find(m => m.id === id))
      .filter((m): m is WorkspaceMember => !!m);
  }, [draftIds, members]);

  const dirty = useMemo(() => {
    const a = new Set(draftIds);
    const b = new Set(settings.board_manager_user_ids);
    if (a.size !== b.size) return true;
    for (const id of a) if (!b.has(id)) return true;
    return false;
  }, [draftIds, settings.board_manager_user_ids]);

  const handleAdd = (userId: string) => {
    setDraftIds(prev => [...prev, userId]);
    setPickerOpen(false);
    setPickerQuery('');
  };
  const handleRemove = (userId: string) => {
    setDraftIds(prev => prev.filter(id => id !== userId));
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await updateWorkspaceSettings(workspace.id, { board_manager_user_ids: draftIds });
      await refresh();
      toast({ title: t`Board managers saved` });
    } catch (err) {
      console.error('[BoardPolicy] 관리자 저장 실패:', err);
      toast({ title: t`Failed to save`, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-card p-5 space-y-4">
      <header className="flex items-center gap-2">
        <UserCog className="size-4 text-primary" />
        <h2 className="text-body font-semibold text-foreground"><Trans>Board managers</Trans></h2>
      </header>
      <p className="text-meta-sm text-muted-foreground leading-relaxed">
        <Trans>Delegate board management permissions (create, edit, delete, manage categories) to members. owner / admin already have these permissions, so there is no need to add them here.</Trans>
      </p>

      {/* 현재 명단 */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 text-meta-sm text-muted-foreground py-3">
            <Loader2 className="size-4 animate-spin" />
            <Trans>Loading members…</Trans>
          </div>
        ) : designatedMembers.length === 0 ? (
          <p className="text-meta-sm text-muted-foreground py-3 italic">
            <Trans>No members delegated yet. Add one below.</Trans>
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {designatedMembers.map(m => (
              <li
                key={m.id}
                className={cn(
                  'inline-flex items-center gap-2 pl-1.5 pr-1 py-1 rounded-full bg-muted/60 border border-border text-label',
                  m.role !== 'member' && 'opacity-60 line-through',
                )}
                title={m.role !== 'member' ? t`Already has ${m.role} permission, so this delegation is ineffective` : undefined}
              >
                <UserAvatar src={m.user.photoUrl} name={m.user.name} className="size-5 shrink-0" />
                <span className="text-meta-sm font-medium text-foreground">{m.user.name}</span>
                <button
                  type="button"
                  onClick={() => handleRemove(m.id)}
                  className="size-5 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                  title={t`Remove`}
                  aria-label={t`Remove ${m.user.name}`}
                >
                  <X className="size-3" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* 추가 — 인라인 picker (member 만, 검색 가능) */}
        <div className="relative">
          {!pickerOpen ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPickerOpen(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              <Trans>Add member</Trans>
            </Button>
          ) : (
            <div className="rounded-lg border border-border bg-background p-2 space-y-1.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                <Input
                  autoFocus
                  value={pickerQuery}
                  onChange={e => setPickerQuery(e.target.value)}
                  placeholder={t`Search by name or email…`}
                  className="pl-8 h-8 text-meta"
                />
                <button
                  type="button"
                  onClick={() => { setPickerOpen(false); setPickerQuery(''); }}
                  className="absolute right-1 top-1/2 -translate-y-1/2 size-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                >
                  <X className="size-3" />
                </button>
              </div>
              <div className="max-h-[220px] overflow-y-auto -mx-1 px-1">
                {candidates.length === 0 ? (
                  <p className="text-meta-sm text-muted-foreground text-center py-4">
                    {members.filter(m => m.role === 'member').length === 0
                      ? t`There are no members in this workspace.`
                      : t`No results found.`}
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    {candidates.map(m => (
                      <li key={m.id}>
                        <button
                          type="button"
                          onClick={() => handleAdd(m.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted text-left cursor-pointer transition-colors"
                        >
                          <UserAvatar src={m.user.photoUrl} name={m.user.name} className="size-6 shrink-0" />
                          <span className="text-label text-foreground truncate">{m.user.name}</span>
                          {m.user.email && (
                            <span className="text-meta-sm text-muted-foreground truncate ml-auto">{m.user.email}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDraftIds(settings.board_manager_user_ids)}
          disabled={!dirty || saving}
        >
          <Trans>Revert</Trans>
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
          {saving && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
          <Trans>Save</Trans>
        </Button>
      </div>
    </section>
  );
};

export default WSAdminBoardPolicyPage;
